import { Injectable, Inject, Renderer2, RendererFactory2 } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { CommonService } from './common.service';

type ScriptLike = HTMLScriptElement | string;

const DEFAULT_TRACKER_PATTERNS: RegExp[] = [
  // Domains / hostnames
  /(^|\.)googletagmanager\.com/i,
  /(^|\.)google-analytics\.com/i,
  /(^|\.)googlesyndication\.com/i,
  /(^|\.)doubleclick\.net/i,
  /(^|\.)googleadservices\.com/i,
  /(^|\.)googleapis\.com\/ajax\/libs\/(gtag|analytics)/i,
  /(^|\.)facebook\.com/i,
  /(^|\.)facebook\.net/i,
  /(^|\.)connect\.facebook\.net/i,

  // Filenames / library names
  /\bgtm\.js\b/i,
  /\bgtag\b/i,
  /\bgtag\(/i,
  /\bgtag\('config'\b/i,
  /\bga\(|\bga\./i,
  /\banalytics\.js\b/i,
  /\badsbygoogle\b/i,
  /\bfbq\(/i,
  /\bfbq\./i,

  // Measurement IDs
  /\bAW-\d{8,}\b/i, // Google Ads conversion IDs
  /\bG-[A-Z0-9]{4,}\b/i, // GA4 IDs
  /\bUA-\d+-\d+\b/i // Universal Analytics IDs
];

@Injectable({
  providedIn: 'root'
})
export class TermlyConsentService {
  private renderer: Renderer2;
  private blockedQueue: Array<{ script: string; type: 'eventPage' | 'orderComplete' }> = [];
  private parentDomainConsent: any = null;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private rendererFactory: RendererFactory2,
    private commonService: CommonService
  ) {
    this.renderer = this.rendererFactory.createRenderer(null, null);
  }

  private listenersAttached = false;
  private retryCount = 0;
  private readonly MAX_RETRIES = 5;

  /**
   * Called when Termly script is loaded. Sets up listeners for consent events.
   */
  private onTermlyLoaded() {
    try {
      this.reactToConsent();
      const termly = (window as any).Termly;
      if (termly && typeof termly.on === 'function') {
        if (!this.listenersAttached) {
          termly?.on('initialized', () => this.reactToConsent());
          termly?.on('consent', () => this.reactToConsent());
          this.listenersAttached = true;
        }
        this.retryCount = 0;
      } else if (this.retryCount < this.MAX_RETRIES) {
        this.retryCount++;
        setTimeout(() => this.onTermlyLoaded(), 150);
      }
    } catch (err) {
      console.warn('[termly] listener registration error:', err);
    }
  }

  /**
   * Injects the Termly resource-blocker script into the document head if not already present.
   */
  public loadTermlyResourceBlocker(): void {
    try {
      if (this.document.querySelector('script[data-name="termly-blocker"]'))
        return;

      const script = this.renderer.createElement('script');
      script.type = 'text/javascript';
      script.src =
        'https://app.termly.io/resource-blocker/6e961a53-a9d1-4fa8-bf22-50bb0ed3b0ad?autoBlock=on';
      script.setAttribute('data-name', 'termly-blocker');
      script.onload = () => {
        this.onTermlyLoaded();
      };
      const head = this.document.head;
      head.insertBefore(script, head.firstChild);
    } catch (e) {
      console.warn('[termly] load blocker script error:', e);
    }
  }

  /**
   * Decides whether to block or immediately inject a pixel script based on its content.
   * If advertising consent is not yet granted, the script is queued unconditionally until consent is given.
   */
  public blockOrderCompletePixel(
    blockedPixel: string,
    type: 'eventPage' | 'orderComplete' = 'eventPage'
  ): void {
    if (!blockedPixel) return;

    if (!this.hasAdsConsentNow()) {
      // Consent not yet granted or explicitly negative -> queue unconditionally
      this.blockedQueue.push({ script: blockedPixel, type });
    } else {
      // Affirmative consent available -> inject immediately
      try {
        if (type === 'orderComplete') {
          this.commonService.scriptInject(blockedPixel);
        } else {
          this.commonService.scriptInject();
        }
      } catch (e) {
        console.warn('[termly] script inject error:', e);
      }
    }
  }

  /**
   * Injects all queued scripts if advertising consent is now available.
   */
  private reactToConsent(): void {
    if (this.hasAdsConsentNow()) {
      // Inject all queued scripts based on consent and type
      this.blockedQueue.forEach(item => {
        try {
          if (item.type === 'orderComplete') {
            this.commonService.scriptInject(item.script);
          } else {
            this.commonService.scriptInject();
          }
        } catch (e) {
          console.warn('[termly] script inject error:', e);
        }
      });
      this.blockedQueue = [];
    }
  }

  /** Extract src/text/outerHTML from either a string or <script> element */
  private extractScriptString(
    scriptLike: ScriptLike
  ): { src?: string; text?: string; outer?: string } {
    if (!scriptLike) return {};
    if (typeof scriptLike === 'string') {
      const s = scriptLike.trim();
      return { outer: s, text: s };
    }
    const el = scriptLike as HTMLScriptElement;
    return {
      src: el.getAttribute('src') || undefined,
      text: el.textContent ? el.textContent.trim() : undefined,
      outer: el.outerHTML
    };
  }

  /**
   * Checks if the user has given advertising consent via Termly.
   */
  private isAdOrTrackingScript(
    scriptLike: ScriptLike,
    extraPatterns: RegExp[] = []
  ): boolean {
    const { src, text, outer } = this.extractScriptString(scriptLike);

    // Respect Termly style markers
    if (typeof scriptLike !== 'string') {
      const el = scriptLike as HTMLScriptElement;
      const dataCats = el.getAttribute('data-categories');
      if (dataCats && /\badvertising\b/i.test(dataCats)) return true;

      const typ = el.getAttribute('type');
      if (typ && /text\/plain|text\/x-placeholder/i.test(typ)) return true;
    }

    const patterns = DEFAULT_TRACKER_PATTERNS.concat(extraPatterns);

    // 1) Check src URL
    if (src) {
      for (const p of patterns) {
        if (p.test(src)) return true;
      }
    }

    // 2) Check inline script text + outerHTML
    const contentToCheck = [text, outer].filter(Boolean).join('\n');
    if (contentToCheck) {
      for (const p of patterns) {
        if (p.test(contentToCheck)) return true;
      }

      // Heuristics
      if (/\bdataLayer\s*=\s*window\.dataLayer\b/i.test(contentToCheck))
        return true;
      if (/\bdataLayer\.push\(/i.test(contentToCheck)) return true;
    }

    return false;
  }

  /** Checks if Termly consent allows ads */
  private hasAdsConsentNow(): boolean {
    try {
      // 1. Fallback to parent domain consent passed via postMessage or mobile
      if (
        this.parentDomainConsent !== null &&
        this.parentDomainConsent !== undefined
      ) {
        if (typeof this.parentDomainConsent === 'boolean') {
          return this.parentDomainConsent;
        }
        if (typeof this.parentDomainConsent === 'object') {
          return !!this.parentDomainConsent.advertising;
        }
      }

      // 2. Check window.Termly consent state
      const termly = (window as any).Termly;
      if (!termly) return false;
      if (typeof termly.getConsentState === 'function') {
        const state = termly.getConsentState();
        return !!state && state.advertising === true;
      }
    } catch (e) {
      console.warn('[termly] consent check error:', e);
    }
    return false;
  }

  public setConsentState(consent: any) {
    this.parentDomainConsent = consent;
    this.reactToConsent();
  }

  public getConsentState() {
    return this.parentDomainConsent;
  }

  public setConsentForMobile(consent: boolean) {
    this.parentDomainConsent = consent;
    this.reactToConsent();
  }

  public getConsentForMobile(): boolean {
    return this.parentDomainConsent === true;
  }
}
